<template>
  <div class="chat-container" id="chatContainer" :class="{ open: isOpen }" >
    <button class="chat-toggle" :class="{ open: isOpen }" @click="toggleChat">
      <span class="chat-icon">{{ isOpen ? '✖' : '💬' }}</span>
      <div class="main-unread-badge" v-if="messageNotReadCount > 0 && !isOpen">
        {{ messageNotReadCount > 99 ? '99+' : messageNotReadCount }}
      </div>
    </button>

    <div class="chat-window" id="chatWindow" :class="{ open: isOpen }">
      <div class="users-sidebar" :class="{ 'mobile-open': isMobileMenuOpen }">
        <div class="users-header">
          Usuários
          <button class="mobile-close-btn" @click="isMobileMenuOpen = false">✕</button>
        </div>
        <div class="users-list">
          <div class="user-item" @click="selectUser(user.id_user)"
               :class="selectedUserId == user.id_user ? 'active' : ''"
               v-for="user in users" :key="user.id_user">
            <div class="user-avatar">👤</div>
            <div class="user-info">
              <div class="user-name-container">
                <div class="user-name">{{ user.user_name }}</div>
                <div class="unread-badge" v-if="user.not_read_count > 0">
                  {{ user.not_read_count }}
                </div>
              </div>
              <div class="user-status" v-if="user.is_connected">Online</div>
              <div class="user-status hidden" v-else>Offline</div>
            </div>
          </div>
        </div>
      </div>

      <div class="mobile-overlay" :class="{ 'active': isMobileMenuOpen }" @click="isMobileMenuOpen = false"></div>

      <div class="chat-area" v-if="selectedUserId">
        <div class="chat-header">
          <button class="hamburger-btn" @click="isMobileMenuOpen = true">
            ☰
          </button>
          <div class="chat-avatar">👤</div>
          <div class="chat-user-info">
            <h3 class="text-white">{{ selectedUserName }}</h3>
          </div>
          <div class="chat-header-actions">
            <button
              class="filter-today-btn"
              :class="{ active: !showTodayOnly }"
              @click="toggleTodayFilter"
              :title="showTodayOnly ? 'Carregar histórico completo' : 'Mostrar apenas mensagens de hoje'"
            >
              📜
            </button>
          </div>
        </div>

        <div class="chat-messages" id="chatMessages" ref="messagesContainer">

          <div class="message" :class="msg.id_user_from == myUserId ? 'sent' : 'received'"
               v-for="msg in filteredMessages" :key="msg.id_chat_message">
            <div class="message-bubble">
              {{ msg.message }}
              <div class="message-time">
                {{ formatTime(msg.sent_at) }}
              </div>
            </div>
          </div>

          <div class="empty-chat" v-if="filteredMessages.length <= 0">
            <div class="empty-chat-icon">💬</div>
            <h3 v-if="!showTodayOnly">Nenhuma mensagem encontrada</h3>
            <h3 v-else>Nenhuma mensagem de hoje</h3>
            <p v-if="showTodayOnly && messages.length > 0">Há mensagens de outros dias disponíveis</p>
          </div>

        </div>

        <div class="chat-input">
          <textarea
            class="message-input"
            v-model="newMessage"
            @keyup.enter.prevent="sendMessage"
            placeholder="Digite sua mensagem... 😊"
            rows="2"
          ></textarea>
          <button class="send-button" @click="sendMessage" :disabled="!newMessage.trim()">
            ➤
          </button>
        </div>
      </div>

      <div class="empty-chat" v-else>
        <div class="empty-chat-icon">💬</div>
        <h3>Selecione um usuário</h3>
        <p>Escolha um usuário para começar a enviar mensagens</p>
        <button class="btn btn-sm btn-primary" v-if="!selectedUserId" @click="isMobileMenuOpen = true">Usuários</button>
      </div>
    </div>

  </div>
</template>

<script>
import http from "@/helpers/http";

export default {
  data() {
    return {
      isOpen: false,
      messageNotReadCount: 0,
      users: [],
      selectedUserId: null,
      selectedUserName: "",
      messages: [],
      newMessage: "",
      notificationSound: new Audio(require('@/assets/sounds/notification.wav')),
      // Novos campos para controle de estado
      isLoadingMessages: false,
      isConnecting: false,
      echoChannel: null,
      presenceChannelName: null,
      onlineUserIds: {},
      pusherPresenceHandlersBound: false,
      reconnectPresenceHandler: null,
      showTodayOnly: true,
      isMobileMenuOpen: false,
      initializationAttempts: 0,
      maxInitializationAttempts: 5
    };
  },
  mounted() {
    document.addEventListener('mousedown', this.handleGlobalClick);

    this.isMobileMenuOpen = this.selectedUserId == null;
    
    Notification.requestPermission().then(permission => {
      console.log('🔔 ChatModal: Permissão de notificação:', permission);
    });
    
    this.$nextTick(() => {
      this.initializeChat();
    });
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleGlobalClick);
    this.unbindPusherPresenceHandlers();
    this.disconnectPresence();
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    myUserId() {
      return this.$store.state.auth.user?.id_user || null;
    },
    filteredMessages() {
      if (!this.showTodayOnly) {
        return this.messages;
      }

      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

      return this.messages.filter(msg => {
        const msgDate = new Date(msg.sent_at);
        return msgDate >= todayStart && msgDate < todayEnd;
      });
    },
  },
  watch: {
    userAuth: {
      handler(newUser, oldUser) {
        if (newUser?.id_user) {
          this.$nextTick(() => {
            this.initializeChat();
          });
        }
      },
      immediate: false
    },
    place: {
      handler(newPlace, oldPlace) {
        if (newPlace?.id_place) {
          this.$nextTick(() => {
            this.initializeChat();
          });
        }
      },
      immediate: false
    }
  },
  methods: {
    buildChannelName(id) {
      return 'chat.' + id;
    },
    buildPresenceChannelName() {
      return `chat.place.${this.place.id_place}`;
    },
    normalizeUserId(id) {
      if (id === null || id === undefined || id === '') {
        return null;
      }

      return Number(id);
    },
    isPresenceChannelSubscribed(channelName) {
      const pusher = typeof Echo !== 'undefined' ? Echo?.connector?.pusher : null;

      if (!pusher) {
        return false;
      }

      const channel = pusher.channel(`presence-${channelName}`);

      return !!channel && channel.subscribed === true;
    },
    disconnectPresence() {
      if (this.presenceChannelName && window.Echo) {
        Echo.leave(this.presenceChannelName);
      }

      this.presenceChannelName = null;
      this.onlineUserIds = {};
    },
    unbindPusherPresenceHandlers() {
      const pusher = typeof Echo !== 'undefined' ? Echo?.connector?.pusher : null;

      if (!pusher || !this.reconnectPresenceHandler) {
        return;
      }

      pusher.connection.unbind('connected', this.reconnectPresenceHandler);
      pusher.connection.unbind('reconnected', this.reconnectPresenceHandler);
      this.reconnectPresenceHandler = null;
      this.pusherPresenceHandlersBound = false;
    },
    ensurePusherPresenceHandlers() {
      const pusher = typeof Echo !== 'undefined' ? Echo?.connector?.pusher : null;

      if (!pusher || this.pusherPresenceHandlersBound) {
        return;
      }

      this.pusherPresenceHandlersBound = true;
      this.reconnectPresenceHandler = () => {
        const channelName = this.buildPresenceChannelName();

        if (this.isPresenceChannelSubscribed(channelName)) {
          return;
        }

        if (this.presenceChannelName) {
          this.presenceChannelName = null;
        }

        this.connectPresence();
      };

      pusher.connection.bind('connected', this.reconnectPresenceHandler);
      pusher.connection.bind('reconnected', this.reconnectPresenceHandler);
    },
    connectPresence() {
      if (!this.place?.id_place || !window.Echo) {
        return;
      }

      this.ensurePusherPresenceHandlers();

      const channelName = this.buildPresenceChannelName();

      if (this.presenceChannelName === channelName && this.isPresenceChannelSubscribed(channelName)) {
        return;
      }

      if (this.presenceChannelName) {
        this.disconnectPresence();
      }

      Echo.join(channelName)
        .here((users) => {
          this.presenceChannelName = channelName;

          const onlineUserIds = {};

          users.forEach((user) => {
            const id = this.normalizeUserId(user?.id_user);

            if (id) {
              onlineUserIds[id] = true;
            }
          });

          this.onlineUserIds = onlineUserIds;
          this.applyOnlineStatus();
        })
        .joining((user) => {
          const id = this.normalizeUserId(user?.id_user);

          if (!id) {
            return;
          }

          this.onlineUserIds = { ...this.onlineUserIds, [id]: true };
          this.applyOnlineStatus();
        })
        .leaving((user) => {
          const id = this.normalizeUserId(user?.id_user);

          if (!id) {
            return;
          }

          const { [id]: left, ...rest } = this.onlineUserIds;
          this.onlineUserIds = rest;
          this.applyOnlineStatus();
        })
        .error((error) => {
          console.error('❌ ChatModal: Erro no canal de presença', error);

          if (this.presenceChannelName === channelName) {
            this.presenceChannelName = null;
          }
        });
    },
    applyOnlineStatus() {
      if (!this.users.length) {
        return;
      }

      this.users = this.mapUsersWithOnlineStatus(this.users);
    },
    mapUsersWithOnlineStatus(users) {
      return users.map((user) => ({
        ...user,
        is_connected: !!this.onlineUserIds[this.normalizeUserId(user.id_user)],
      }));
    },
    pusherChannelName(name) {
      return 'private-' + name;
    },
    isWsConnected() {
      const pusher = typeof Echo !== 'undefined' ? Echo?.connector?.pusher : null;
      return !!pusher && pusher.connection.state === 'connected';
    },
    isChannelSubscribed(name) {
      const pusher = typeof Echo !== 'undefined' ? Echo?.connector?.pusher : null;
      if (!pusher) return false;
      const chan = pusher.channel(this.pusherChannelName(name));
      return !!chan && chan.subscribed === true;
    },
    ensureWsAndChannel() {
      const uid = this.$store.state.auth.user?.id_user;
      if (!uid || !window.Echo) return;
      const name = this.buildChannelName(uid);
      const needConnect = !this.isWsConnected() || !this.isChannelSubscribed(name) || this.echoChannel !== name;

      if (needConnect) {
        this.wsConnect();
      }

      this.connectPresence();
    },
    initializeChat() {
      this.initializationAttempts++;
      console.log('✅ ChatModal: Inicializando chat');

      if (!this.place?.id_place) {
        console.warn('⚠️ ChatModal: Place não disponível, aguardando...', {
          place: this.place,
          attempt: this.initializationAttempts,
          timestamp: new Date().toISOString()
        });
        
        if (this.initializationAttempts < this.maxInitializationAttempts) {
          console.log('🔄 ChatModal: Tentando novamente em 1 segundo...');
          setTimeout(() => this.initializeChat(), 1000);
        } else {
          console.error('❌ ChatModal: Máximo de tentativas de inicialização atingido');
        }
        return;
      }

      if (!this.userAuth?.id_user) {
        console.warn('⚠️ ChatModal: Usuário não autenticado, aguardando...', {
          user: this.userAuth,
          attempt: this.initializationAttempts,
          timestamp: new Date().toISOString()
        });
        
        if (this.initializationAttempts < this.maxInitializationAttempts) {
          console.log('🔄 ChatModal: Tentando novamente em 1 segundo...');
          setTimeout(() => this.initializeChat(), 1000);
        } else {
          console.error('❌ ChatModal: Máximo de tentativas de inicialização atingido');
        }
        return;
      }

      this.loadUsers();

      console.log('✅ ChatModal: Conectando canal de presença');
      this.connectPresence();
      
      console.log('✅ ChatModal: Conectando WebSocket');
      this.wsConnect();
      
      console.log('✅ ChatModal: Carregando estatísticas');
      this.getNotReadCount();

      this.initializationAttempts = 0;
    },

    showNotification(data) {
      console.log('🔔 ChatModal: Dados da notificação:', {
        permission: Notification.permission,
        data: data,
        timestamp: new Date().toISOString()
      });

      if (Notification.permission === "granted") {
        const user = this.users.find(u => u.id_user == data.id_user_from);

        if (user) {
          new Notification("Nova mensagem 💬", {
            body: `${user.user_name}: ${data.message}`,
            icon: require('@/assets/images/logo_ubbi.png'),
          });
        } else {
          new Notification("Nova mensagem 💬", {
            body: `Nova mensagem recebida`,
            icon: require('@/assets/images/logo_ubbi.png'),
          });
        }
      } else {
        console.warn('⚠️ ChatModal: Permissão de notificação não concedida:', Notification.permission);
      }
    },
    playNotification() {
      this.notificationSound.currentTime = 0;
      
      this.notificationSound.play().catch((error) => {
        console.log('🔊 ChatModal: Erro ao reproduzir áudio:', {
          error: error,
          error_message: error?.message,
          timestamp: new Date().toISOString()
        });
        console.log("Usuário não está ativo na página.");
      });
    },
    getNotReadCount() {
      http.get(`/places/${this.place.id_place}/chat/statistics`)
        .then((r) => {
          this.messageNotReadCount = r.data.not_read_count;
        })
        .catch((error) => {
          console.error('❌ ChatModal: Erro ao carregar estatísticas do chat');
          console.error('❌ ChatModal: Detalhes do erro:', {
            error: error,
            error_message: error?.message,
            error_response: error?.response?.data,
            status_code: error?.response?.status,
            place_id: this.place?.id_place,
            timestamp: new Date().toISOString()
          });
        });
    },
    toggleChat() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.ensureWsAndChannel();
        this.$nextTick(() => {
          this.loadMessages();
          this.scrollToBottom();
        });
      } else {
        this.getNotReadCount();
      }
    },
    wsConnect() {
      const user = this.$store.state.auth.user;
      
      console.log('🔌 ChatModal: Iniciando conexão WebSocket');

      // Validação das dependências do WebSocket
      if (!user?.id_user) {
        console.error('❌ ChatModal: Usuário não disponível para WebSocket', {
          user: user,
          timestamp: new Date().toISOString()
        });
        return;
      }

      if (!window.Echo) {
        console.error('❌ ChatModal: Echo não disponível', {
          echo_available: !!window.Echo,
          timestamp: new Date().toISOString()
        });
        return;
      }

      const channelName = this.buildChannelName(user.id_user);
      if (this.isWsConnected() && this.isChannelSubscribed(channelName)) {
        this.echoChannel = channelName;
        this.connectPresence();
        return;
      }

      try {
        this.echoChannel = channelName;
        console.log(`🔌 ChatModal: Conectando ao canal: ${channelName}`);
        console.log('🔌 ChatModal: Echo disponível:', !!window.Echo);
        console.log('🔌 ChatModal: Echo.private disponível:', typeof Echo.private);

        const channel = Echo.private(channelName);
        console.log('🔌 ChatModal: Canal criado:', channel);

        // Listeners de eventos
        channel
          .listen('ChatUpdated', (event) => {
            console.log('📨 ChatModal: Nova mensagem recebida');
            console.log('📨 ChatModal: Dados do evento:', {
              event_type: 'ChatUpdated',
              data: JSON.stringify(event.data),
              timestamp: new Date().toISOString(),
              chat_open: this.isOpen,
              selected_user: this.selectedUserId
            });
            this.handleNewMessage(event.data);

            if (!this.isOpen) {
              console.log('📨 ChatModal: Chat fechado, atualizando contador');
              this.getNotReadCount();
            }
          })
          .listen('ChatStatistics', (event) => {
            console.log('📊 ChatModal: Estatísticas atualizadas');
            console.log('📊 ChatModal: Dados das estatísticas:', {
              event_type: 'ChatStatistics',
              data: JSON.stringify(event.data),
              timestamp: new Date().toISOString(),
              previous_count: this.messageNotReadCount
            });
            this.messageNotReadCount = event.data.not_read_count;
          })
          .error((error) => {
            console.error('❌ ChatModal: Erro na conexão WebSocket');
            console.error('❌ ChatModal: Detalhes do erro:', {
              error: error,
              error_message: error?.message,
              error_stack: error?.stack,
              channel: channelName,
              timestamp: new Date().toISOString()
            });
          });

        // Monitora o estado da conexão do Echo
        if (Echo.connector && Echo.connector.pusher) {
          const pusher = Echo.connector.pusher;
          console.log('🔌 ChatModal: Pusher disponível:', !!pusher);
          console.log('🔌 ChatModal: Estado da conexão:', pusher.connection.state);

          // Log imediato do estado atual
          console.log('✅ ChatModal: WebSocket conectado');
          console.log('✅ ChatModal: Detalhes da conexão:', {
            state: pusher.connection.state,
            socket_id: pusher.connection.socket_id,
            timestamp: new Date().toISOString()
          });

          // Evento de conexão estabelecida
          pusher.connection.bind('connected', () => {
            console.log('✅ ChatModal: WebSocket conectado (evento)');
            console.log('✅ ChatModal: Detalhes da conexão:', {
              state: pusher.connection.state,
              socket_id: pusher.connection.socket_id,
              timestamp: new Date().toISOString()
            });
          });

          // Evento de desconexão
          pusher.connection.bind('disconnected', () => {
            console.warn('⚠️ ChatModal: WebSocket desconectado');
            console.warn('⚠️ ChatModal: Detalhes da desconexão:', {
              state: pusher.connection.state,
              timestamp: new Date().toISOString()
            });
          });

          // Evento de erro na conexão
          pusher.connection.bind('error', (error) => {
            console.error('❌ ChatModal: Erro no WebSocket');
            console.error('❌ ChatModal: Detalhes do erro:', {
              error: error,
              error_message: error?.message,
              error_code: error?.code,
              state: pusher.connection.state,
              timestamp: new Date().toISOString()
            });
          });

          // Evento de tentativa de reconexão
          pusher.connection.bind('connecting', () => {
            console.log('🔄 ChatModal: Tentando reconectar WebSocket...');
            console.log('🔄 ChatModal: Estado durante reconexão:', {
              state: pusher.connection.state,
              timestamp: new Date().toISOString()
            });
          });

          // Evento de reconexão bem-sucedida
          pusher.connection.bind('reconnected', () => {
            console.log('✅ ChatModal: WebSocket reconectado com sucesso');
            console.log('✅ ChatModal: Detalhes da reconexão:', {
              state: pusher.connection.state,
              socket_id: pusher.connection.socket_id,
              timestamp: new Date().toISOString()
            });
          });
        } else {
          console.warn('⚠️ ChatModal: Pusher não disponível');
          console.warn('⚠️ ChatModal: Echo.connector:', !!Echo.connector);
          console.warn('⚠️ ChatModal: Echo.connector.pusher:', !!Echo.connector?.pusher);
        }

        console.log('✅ ChatModal: Conexão WebSocket estabelecida com sucesso');
        console.log('✅ ChatModal: Canal configurado:', {
          channel_name: channelName,
          echo_channel: this.echoChannel,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('❌ ChatModal: Erro ao conectar WebSocket');
        console.error('❌ ChatModal: Detalhes do erro:', {
          error: error,
          error_message: error?.message,
          error_stack: error?.stack,
          channel_name: channelName,
          user_id: user?.id_user,
          timestamp: new Date().toISOString()
        });
      }
    },

    // Tratamento de nova mensagem com validações
    handleNewMessage(data) {
      console.log('📨 ChatModal: Processando nova mensagem');
      console.log('📨 ChatModal: Dados recebidos:', {
        data: data,
        data_valid: !!(data && data.id_user_from),
        id_user_from: data?.id_user_from,
        message: data?.message?.substring(0, 50) + (data?.message?.length > 50 ? '...' : ''),
        timestamp: new Date().toISOString()
      });

      if (!data || !data.id_user_from) {
        console.warn('⚠️ ChatModal: Dados de mensagem inválidos');
        console.warn('⚠️ ChatModal: Dados recebidos:', data);
        return;
      }

      console.log('📨 ChatModal: Estado atual do chat:', {
        selected_user_id: this.selectedUserId,
        chat_open: this.isOpen,
        message_from: data.id_user_from,
        is_current_conversation: this.selectedUserId == data.id_user_from,
        timestamp: new Date().toISOString()
      });

      console.log('📨 ChatModal: Recarregando usuários');
      this.loadUsers();

      // Recarrega mensagens apenas se necessário
      if (this.selectedUserId == data.id_user_from && this.isOpen) {
        console.log('📨 ChatModal: É a conversa atual e chat aberto, recarregando mensagens');
        this.loadMessages();
      }

      console.log('📨 ChatModal: Reproduzindo notificação');
      this.playNotification();
      console.log('📨 ChatModal: Exibindo notificação');
      this.showNotification(data);
    },

    loadUsers() {
      console.log('👥 ChatModal: Carregando usuários do chat');
      console.log('👥 ChatModal: Dados da requisição:', {
        place_id: this.place?.id_place,
        url: `/places/${this.place.id_place}/chat/users`,
        current_users_count: this.users.length,
        timestamp: new Date().toISOString()
      });

      http.get(`/places/${this.place.id_place}/chat/users`)
        .then((response) => {
          console.log('✅ ChatModal: Usuários carregados com sucesso');
          console.log('👥 ChatModal: Dados recebidos:', {
            users_count: response.data.length,
            previous_count: this.users.length,
            users_data: response.data.map(user => ({
              id_user: user.id_user,
              user_name: user.user_name,
              is_connected: user.is_connected,
              not_read_count: user.not_read_count
            })),
            timestamp: new Date().toISOString()
          });
          this.users = this.mapUsersWithOnlineStatus(response.data);
          console.log('✅ ChatModal: Usuários atualizados no estado:', this.users.length);
        })
        .catch((error) => {
          console.error('❌ ChatModal: Erro ao carregar usuários');
          console.error('❌ ChatModal: Detalhes do erro:', {
            error: error,
            error_message: error?.message,
            error_response: error?.response?.data,
            status_code: error?.response?.status,
            place_id: this.place?.id_place,
            timestamp: new Date().toISOString()
          });
          this.$toast?.error('Erro ao carregar usuários do chat');
        });
    },
    selectUser(userId) {
      this.selectedUserId = userId;
      const user = this.users.find(u => u.id_user == userId);
      this.selectedUserName = user ? user.user_name : "";

      this.isMobileMenuOpen = false;
      this.loadMessages();
    },
    loadMessages() {
      if (!this.selectedUserId) {
        console.warn('⚠️ ChatModal: Usuário não selecionado para carregar mensagens');
        return;
      }

      if (this.isLoadingMessages) {
        return;
      }

      this.isLoadingMessages = true;

      const url = `/places/${this.place.id_place}/chat/messages/${this.selectedUserId}`;
      console.log('🌐 ChatModal: URL da requisição:', url);

      http.get(url)
        .then(res => {
          this.messages = res.data;
          console.log('✅ ChatModal: Mensagens atualizadas no estado:', this.messages.length);

          this.markAsRead();
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        })
        .catch((error) => {
          console.error('❌ ChatModal: Detalhes do erro:', {
            error: error,
            error_message: error?.message,
            error_response: error?.response?.data,
            status_code: error?.response?.status,
            selected_user_id: this.selectedUserId,
            place_id: this.place?.id_place,
            timestamp: new Date().toISOString()
          });
          this.$toast?.error('Erro ao carregar mensagens');
        })
        .finally(() => {
          this.isLoadingMessages = false;
        });
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      
      if (container) {
        console.log('📜 ChatModal: Container encontrado:', {
          scroll_height: container.scrollHeight,
          client_height: container.clientHeight,
          scroll_top_before: container.scrollTop,
          timestamp: new Date().toISOString()
        });
        
        container.scrollTop = container.scrollHeight;
      } else {
        console.warn('⚠️ ChatModal: Container de mensagens não encontrado');
      }
    },
    markAsRead() {
      if (!this.selectedUserId) {
        console.warn('⚠️ ChatModal: Usuário não selecionado para marcar como lido');
        return;
      }

      http.patch(`/places/${this.place.id_place}/chat/messages/mark-as-read/${this.selectedUserId}`)
        .then(() => {
          console.log('✅ ChatModal: Mensagens marcadas como lidas com sucesso');
          console.log('👁️ ChatModal: Recarregando usuários após marcar como lido');
          this.loadUsers();
        })
        .catch((error) => {
          console.error('❌ ChatModal: Erro ao marcar mensagens como lidas');
          console.error('❌ ChatModal: Detalhes do erro:', {
            error: error,
            error_message: error?.message,
            error_response: error?.response?.data,
            status_code: error?.response?.status,
            selected_user_id: this.selectedUserId,
            place_id: this.place?.id_place,
            timestamp: new Date().toISOString()
          });
        });
    },
    sendMessage() {
      console.log('📤 ChatModal: Iniciando envio de mensagem');
      console.log('📤 ChatModal: Estado atual:', {
        new_message: this.newMessage,
        message_length: this.newMessage?.length,
        selected_user_id: this.selectedUserId,
        selected_user_name: this.selectedUserName,
        place_id: this.place?.id_place,
        timestamp: new Date().toISOString()
      });

      if (!this.newMessage?.trim()) {
        console.log('⚠️ ChatModal: Mensagem vazia, cancelando envio');
        return;
      }

      if (!this.selectedUserId) {
        console.warn('⚠️ ChatModal: Usuário não selecionado para enviar mensagem');
        this.$toast?.error('Erro: usuário não selecionado');
        return;
      }

      const messageText = this.newMessage.trim();
      console.log('📤 ChatModal: Texto da mensagem:', {
        original: this.newMessage,
        trimmed: messageText,
        length: messageText.length
      });

      this.newMessage = ""; // Limpa o campo imediatamente para melhor UX
      console.log('📤 ChatModal: Campo de mensagem limpo');

      const payload = {
        id_user_to: this.selectedUserId,
        message: messageText,
      };

      console.log('📤 ChatModal: Payload da requisição:', {
        payload: payload,
        url: `/places/${this.place.id_place}/chat/messages`,
        timestamp: new Date().toISOString()
      });

      http.post(`/places/${this.place.id_place}/chat/messages`, payload)
        .then(() => {
          console.log('✅ ChatModal: Mensagem enviada com sucesso');
          console.log('📤 ChatModal: Recarregando mensagens após envio');
          this.loadMessages();
          this.$nextTick(() => {
            console.log('⏭️ ChatModal: NextTick executado, fazendo scroll após envio');
            this.scrollToBottom();
          });
        })
        .catch((error) => {
          console.error('❌ ChatModal: Erro ao enviar mensagem');
          console.error('❌ ChatModal: Detalhes do erro:', {
            error: error,
            error_message: error?.message,
            error_response: error?.response?.data,
            status_code: error?.response?.status,
            payload: payload,
            selected_user_id: this.selectedUserId,
            place_id: this.place?.id_place,
            timestamp: new Date().toISOString()
          });
          this.$toast?.error('Erro ao enviar mensagem. Tente novamente.');
          // Restaura a mensagem em caso de erro
          this.newMessage = messageText;
          console.log('📤 ChatModal: Mensagem restaurada após erro:', this.newMessage);
        });
    },
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    toggleTodayFilter() {
      console.log('📅 ChatModal: Toggle do filtro de hoje');
      console.log('📅 ChatModal: Estado anterior:', {
        show_today_only: this.showTodayOnly,
        total_messages: this.messages.length,
        filtered_messages: this.filteredMessages.length,
        timestamp: new Date().toISOString()
      });

      this.showTodayOnly = !this.showTodayOnly;
      console.log('📅 ChatModal: Estado após toggle:', {
        show_today_only: this.showTodayOnly,
        timestamp: new Date().toISOString()
      });

      console.log('📅 ChatModal: Mensagens após filtro:', {
        total_messages: this.messages.length,
        filtered_messages: this.filteredMessages.length,
        filter_active: this.showTodayOnly
      });

      this.$nextTick(() => {
        console.log('⏭️ ChatModal: NextTick executado após toggle do filtro');
        this.scrollToBottom();
      });
    },
    handleGlobalClick(e) {
      if (!this.isOpen) return;
      const chatWindow = this.$el.querySelector('.chat-window');
      const chatToggle = this.$el.querySelector('.chat-toggle');
      if (
          chatWindow &&
          !chatWindow.contains(e.target) &&
          chatToggle &&
          !chatToggle.contains(e.target)
      ) {
        this.isOpen = false;
        this.getNotReadCount();
      }
    },
  },
};
</script>

<style scoped>
.main-unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #dc3545;
  color: white;
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
  line-height: 16px;
  border: 2px solid white;
  animation: bounce-in 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.unread-badge {
  background: #780f5a;
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  line-height: 16px;
  animation: pulse 0.3s ease;
  flex-shrink: 0;
}

.user-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-item.active .unread-badge {
  display: none;
}

.chat-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  z-index: 1000;
}

.chat-container.open {
  width: 600px;
  height: 500px;
}

.chat-toggle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #780f5a 0%, #9d1b6c 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 25px rgba(120, 15, 90, 0.3);
  transition: all 0.3s ease;
  z-index: 1001;
}

.chat-icon {
  font-size: 26px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-toggle:hover {
  transform: scale(1.05) translateY(-1px);
  box-shadow: 0 10px 30px rgba(120, 15, 90, 0.4);
}

.chat-toggle.open {
  background: #ff6b6b;
  border-radius: 50%;
}

.chat-window {
  opacity: 0;
  visibility: hidden;
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 600px;
  height: 500px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: all 0.3s ease;
  transform: translateY(20px) scale(0.95);
  display: flex;
}

.chat-window.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

/* Users List Sidebar */
.users-sidebar {
  width: 200px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
}

.mobile-close-btn {
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
}

.mobile-overlay {
  display: none;
}

.hamburger-btn {
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
}

.users-header {
  background: #780f5a;
  color: white;
  padding: 20px;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.users-list {
  flex: 1;
  overflow-y: auto;
}

.user-item {
  padding: 15px 5px 15px 20px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-item:hover {
  background-color: #e9ecef;
}

.user-item.active {
  background-color: #780f5a;
  color: white;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #780f5a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
  flex-shrink: 0;
}

.user-item.active .user-avatar {
  background: rgba(255, 255, 255, 0.2);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.user-status {
  font-size: 11px;
  opacity: 0.7;
}

/* Chat Area */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chat-header {
  background: #780f5a;
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.chat-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-today-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
}

.filter-today-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.filter-today-btn.active {
  background: rgba(255, 255, 255, 0.9);
  color: #780f5a;
  border-color: rgba(255, 255, 255, 0.9);
}

.chat-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.chat-user-info h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: white;
}

.message {
  margin-bottom: 16px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.received .message-bubble {
  background: #f8f9fa;
  color: #333;
  margin-right: 60px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.message.sent {
  display: flex;
  justify-content: flex-end;
}

.message.sent .message-bubble {
  background: #780f5a;
  color: white;
  margin-left: 60px;
  border-bottom-right-radius: 4px;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 18px;
  max-width: 200px;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.4;
}

.message-time {
  font-size: 11px;
  opacity: 0.6;
  margin-top: 4px;
}

.chat-input {
  padding: 20px;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.message-input {
  flex: 1;
  border: 2px solid #f0f0f0;
  border-radius: 25px;
  padding: 12px 16px;
  font-size: 14px;
  outline: none;
  resize: none;
  min-height: 20px;
  max-height: 80px;
  font-family: inherit;
  transition: border-color 0.3s ease;
}

.message-input:focus {
  border-color: #780f5a;
}

.send-button {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #780f5a;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.send-button:hover {
  transform: scale(1.05);
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Empty state */
.empty-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  text-align: center;
  padding: 40px;
}

.empty-chat-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-chat h3 {
  margin-bottom: 8px;
  font-size: 18px;
}

.empty-chat p {
  opacity: 0.7;
}

/* Scrollbar styling */
.users-list::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.users-list::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.users-list::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.users-list::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

/* Tablet responsiveness */
@media (max-width: 768px) {
  .chat-container.open {
    width: 500px;
    height: 450px;
  }

  .chat-window {
    width: 500px;
    height: 450px;
  }

  .users-sidebar {
    width: 160px;
  }

  .message.received .message-bubble {
    margin-right: 40px;
  }

  .message.sent .message-bubble {
    margin-left: 40px;
  }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .chat-container {
    bottom: 10px;
    right: 10px;
  }

  .chat-container.open {
    width: calc(100vw - 20px);
    height: calc(100vh - 80px);
    bottom: 10px;
    right: 10px;
  }

  .chat-window {
    width: 100%;
    height: 100%;
    bottom: 70px;
    right: 0;
    border-radius: 15px;
  }

  /* Menu hamburger mobile */
  .hamburger-btn {
    display: flex;
  }

  .mobile-close-btn {
    display: flex;
    margin-left: auto;
  }

  .mobile-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    z-index: 999;
  }

  .mobile-overlay.active {
    opacity: 1;
    visibility: visible;
  }

  .users-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    width: 280px;
    height: 100%;
    z-index: 1000;
    transform: translateX(-100%);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }

  .users-sidebar.mobile-open {
    transform: translateX(0);
  }

  .users-header {
    padding: 20px 15px;
    font-size: 16px;
    justify-content: space-between;
  }

  .user-item {
    padding: 15px;
    flex-direction: row;
    gap: 12px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .user-info {
    text-align: left;
    width: auto;
  }

  .user-name-container {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .user-name {
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    max-width: 150px;
  }

  .user-status {
    font-size: 12px;
  }

  .unread-badge {
    font-size: 11px;
    padding: 2px 6px;
    min-width: 20px;
  }

  .chat-header {
    padding: 15px;
  }

  .chat-avatar {
    width: 35px;
    height: 35px;
    font-size: 16px;
  }

  .chat-user-info h3 {
    font-size: 14px;
  }

  .filter-today-btn {
    padding: 6px 10px;
    font-size: 14px;
    min-width: 36px;
    height: 36px;
  }

  .chat-messages {
    padding: 15px;
  }

  .message.received .message-bubble {
    margin-right: 20px;
  }

  .message.sent .message-bubble {
    margin-left: 20px;
  }

  .message-bubble {
    max-width: 70%;
    font-size: 13px;
  }

  .chat-input {
    padding: 15px;
  }

  .message-input {
    font-size: 13px;
    padding: 10px 14px;
  }

  .send-button {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
}
</style>